import { useState, useCallback } from 'react';
import type { UseAdminAppReturn } from './useAdminApp';
import { identifierSchemas } from '@pineseed/spec';
import type { z } from 'zod';

// Extract types from schemas
type ParticipantUserMapping = z.infer<typeof identifierSchemas.ParticipantUserMapping>;
type LinkSource = z.infer<typeof identifierSchemas.LinkSource>;
type DeviceInfo = z.infer<typeof identifierSchemas.DeviceInfo>;

interface LinkMappingRequest {
  participantId: string;
  userId: string;
  linkSource: LinkSource;
  deviceInfo?: DeviceInfo;
}

interface MappingStats {
  totalMappings: number;
  byLinkSource: Record<LinkSource, number>;
  recentMappings: ParticipantUserMapping[];
}

export interface IdentifierAdminHandler {
  // State
  mappings: ParticipantUserMapping[];
  selectedMapping: ParticipantUserMapping | null;
  loading: boolean;
  error: string | null;
  
  // Lookup operations
  getUserByParticipant: (participantId: string) => Promise<{ userId: string; participantId: string } | null>;
  getParticipantsByUser: (userId: string) => Promise<{ userId: string; participantIds: string[]; total: number }>;
  getMappingByParticipant: (participantId: string) => Promise<ParticipantUserMapping | null>;
  getMappingsByUser: (userId: string) => Promise<ParticipantUserMapping[]>;
  
  // Link/Unlink operations
  linkParticipantToUser: (request: LinkMappingRequest) => Promise<ParticipantUserMapping>;
  unlinkParticipant: (participantId: string) => Promise<boolean>;
  unlinkAllUserParticipants: (userId: string) => Promise<number>;
  
  // Utility operations
  isLinked: (participantId: string) => Promise<boolean>;
  countMappings: (userId?: string) => Promise<number>;
  getAllMappings: (limit?: number, offset?: number) => Promise<void>;
  
  // Stats
  getStats: () => MappingStats;
  
  // Clear state
  clearState: () => void;
}

export function useIdentifierAdmin(adminApp: UseAdminAppReturn): IdentifierAdminHandler {
  const [mappings, setMappings] = useState<ParticipantUserMapping[]>([]);
  const [selectedMapping, setSelectedMapping] = useState<ParticipantUserMapping | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserByParticipant = useCallback(async (participantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/user?participantId=${encodeURIComponent(participantId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get user by participant: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Got user by participant:', data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get user by participant';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Get user by participant error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const getParticipantsByUser = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/participants?userId=${encodeURIComponent(userId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get participants by user: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Got participants by user:', data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get participants by user';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Get participants by user error:', errorMessage);
      return { userId, participantIds: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const getMappingByParticipant = useCallback(async (participantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/mapping?participantId=${encodeURIComponent(participantId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get mapping by participant: ${response.statusText}`);
      }

      const data = await response.json() as ParticipantUserMapping;
      console.log('✅ [IdentifierAdmin] Got mapping by participant:', data);
      setSelectedMapping(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get mapping by participant';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Get mapping by participant error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const getMappingsByUser = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/mappings?userId=${encodeURIComponent(userId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get mappings by user: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Got mappings by user:', data);
      const userMappings = data.mappings || [];
      setMappings(userMappings);
      return userMappings;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get mappings by user';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Get mappings by user error:', errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const linkParticipantToUser = useCallback(async (request: LinkMappingRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/link`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'MAPPING_CONFLICT') {
          throw new Error(`Mapping conflict: ${errorData.message}`);
        }
        throw new Error(`Failed to link participant to user: ${response.statusText}`);
      }

      const data = await response.json() as ParticipantUserMapping;
      console.log('✅ [IdentifierAdmin] Linked participant to user:', data);
      
      // Add to mappings list
      setMappings(prev => [...prev, data]);
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link participant to user';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Link participant to user error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const unlinkParticipant = useCallback(async (participantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/unlink?participantId=${encodeURIComponent(participantId)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (response.status === 404) {
        console.warn('⚠️ [IdentifierAdmin] Mapping not found for participant:', participantId);
        return false;
      }

      if (!response.ok) {
        throw new Error(`Failed to unlink participant: ${response.statusText}`);
      }

      console.log('✅ [IdentifierAdmin] Unlinked participant:', participantId);
      
      // Remove from mappings list
      setMappings(prev => prev.filter(m => m.participantId !== participantId));
      if (selectedMapping?.participantId === participantId) {
        setSelectedMapping(null);
      }
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlink participant';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Unlink participant error:', errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, selectedMapping?.participantId]);

  const unlinkAllUserParticipants = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/unlink-all?userId=${encodeURIComponent(userId)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to unlink all user participants: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Unlinked all participants for user:', data);
      
      // Remove all mappings for this user
      setMappings(prev => prev.filter(m => m.userId !== userId));
      
      return data.deletedCount || 0;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlink all user participants';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Unlink all user participants error:', errorMessage);
      return 0;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const isLinked = useCallback(async (participantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/is-linked?participantId=${encodeURIComponent(participantId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to check if linked: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Checked if linked:', data);
      return data.isLinked || false;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check if linked';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Is linked error:', errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const countMappings = useCallback(async (userId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = userId
        ? `${adminApp.httpUrl}/api/identifier/count?userId=${encodeURIComponent(userId)}`
        : `${adminApp.httpUrl}/api/identifier/count`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to count mappings: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Counted mappings:', data);
      return data.count || 0;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to count mappings';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Count mappings error:', errorMessage);
      return 0;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const getAllMappings = useCallback(async (limit = 100, offset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/identifier/all?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get all mappings: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [IdentifierAdmin] Got all mappings:', data);
      setMappings(data.mappings || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get all mappings';
      setError(errorMessage);
      console.error('❌ [IdentifierAdmin] Get all mappings error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const getStats = useCallback(() => {
    // Calculate stats from current mappings in state (synchronous)
    const byLinkSource: Record<LinkSource, number> = {
      login: 0,
      signup: 0,
      manual: 0,
      migration: 0,
    };
    
    mappings.forEach(m => {
      byLinkSource[m.linkSource] = (byLinkSource[m.linkSource] || 0) + 1;
    });
    
    // Sort by most recent
    const recentMappings = [...mappings]
      .sort((a, b) => new Date(b.linkedAt).getTime() - new Date(a.linkedAt).getTime())
      .slice(0, 10);
    
    return {
      totalMappings: mappings.length,
      byLinkSource,
      recentMappings,
    };
  }, [mappings]);

  const clearState = useCallback(() => {
    setMappings([]);
    setSelectedMapping(null);
    setError(null);
  }, []);

  return {
    mappings,
    selectedMapping,
    loading,
    error,
    getUserByParticipant,
    getParticipantsByUser,
    getMappingByParticipant,
    getMappingsByUser,
    linkParticipantToUser,
    unlinkParticipant,
    unlinkAllUserParticipants,
    isLinked,
    countMappings,
    getAllMappings,
    getStats,
    clearState,
  };
}

